import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Bus, Mail, Lock, ArrowLeft, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusOperatorLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.phone || !formData.password) {
      toast({
        title: 'Missing Information',
        description: 'Please enter phone and password',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Use regular auth login
      const response = await api.post('/auth/login', {
        phone: `+260${formData.phone.replace(/\D/g, '')}`,
        password: formData.password
      });

      localStorage.setItem('tekstar_token', response.data.token);
      localStorage.setItem('tekstar_user', JSON.stringify(response.data.user));

      // Check if user is a bus operator
      try {
        await api.get('/bus/operators/profile');
        navigate('/bus/operator');
      } catch (error) {
        if (error.response?.status === 404) {
          toast({
            title: 'Not Registered',
            description: 'No operator account found. Please register first.',
          });
          navigate('/bus/operator/apply');
        } else {
          throw error;
        }
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.response?.data?.detail || 'Invalid credentials',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <Bus className="h-8 w-8 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold">Bus Operator Login</h1>
          <p className="text-gray-500">Access your operator dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Phone Number</Label>
            <div className="flex mt-1">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm font-medium" data-testid="phone-prefix">+260</span>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                placeholder="XXX XXX XXX"
                maxLength={9}
                type="tel"
                className="rounded-l-none"
                data-testid="login-phone"
              />
            </div>
          </div>

          <div>
            <Label>Password</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
                className="pr-10"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button 
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-500 text-sm">
            Don't have an account?{' '}
            <Link to="/bus/operator/apply" className="text-indigo-600 hover:underline font-medium">
              Register as Operator
            </Link>
          </p>
        </div>

        <div className="mt-4 pt-4 border-t text-center">
          <Link to="/bus" className="text-gray-500 text-sm hover:text-gray-700 flex items-center justify-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to Bus Search
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default BusOperatorLogin;
